import { Alert } from "@msuite/ui/alert";
import { DateInput } from "@msuite/ui/date-input";
import { Divider } from "@msuite/ui/divider";
import {
	FormCheckbox,
	FormSwitch,
	InlineSelect,
	Select,
	Textarea,
	TextInput,
} from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { ALERT_LEVEL_TYPES } from "@msuite/utils/alerts";
import type { AlertFormSchema } from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useTeams } from "@/hooks/use-teams";

export function AlertForm() {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<AlertFormSchema>();
	const mentionOptions = useMentionOptions();
	const hasDateLimit = useWatch({ control: methods.control, name: "hasDateLimit" });

	/** Query */
	const { teams } = useTeams();

	/** Computed */
	const teamOptions = useMemo(() => {
		const seen = new Set<string>();
		const options: { value: string; label: string }[] = [];
		for (const team of teams) {
			if (seen.has(team.teamId)) continue;
			seen.add(team.teamId);
			options.push({ value: team.teamId, label: guardString(team.name) });
		}
		return options;
	}, [teams]);

	/** Render */
	return (
		<div className="v-stack gap-4">
			<TextInput
				control={methods.control}
				name="title"
				label={t("protocols.title")}
				isRequired
				placeholder={t("placeholders.title")}
			/>
			<Textarea
				control={methods.control}
				name="description"
				label={t("general.description")}
				placeholder={t("placeholders.description")}
				isRequired
				disableFormatting
				{...mentionOptions}
			/>
			<Select
				isRequired
				label={t("dashboard.alerts.alert_level")}
				control={methods.control}
				name="levelType"
				itemNotFoundLabel="Not Found"
				isNullable={false}
				options={ALERT_LEVEL_TYPES.map((level) => ({
					label: level,
					value: level,
					item: { level },
				}))}
				renderOption={(option) => <Alert.Label level={option.level} />}
			/>
			<InlineSelect
				control={methods.control}
				name="teamIds"
				label={t("dashboard.alerts.teams")}
				isRequired={false}
				options={teamOptions}
			/>
			<FormCheckbox
				control={methods.control}
				name="isDismissable"
			>
				{t("dashboard.alerts.user_can_dismiss_alert")}
			</FormCheckbox>
			<Divider />
			<TextInput
				control={methods.control}
				name="href"
				label={t("general.link")}
				placeholder={t("placeholders.web")}
				isRequired={false}
				disableFormatting
				{...mentionOptions}
			/>
			<Divider />
			<FormSwitch
				control={methods.control}
				name="hasDateLimit"
				width="full"
			>
				Has date limit
			</FormSwitch>
			{hasDateLimit && (
				<div className="grid grid-cols-2 gap-x-4">
					<DateInput
						label={t("dates.start_date")}
						isRequired
						control={methods.control}
						name="startDate"
					/>
					<DateInput
						label={t("dates.start_date")}
						isRequired
						control={methods.control}
						name="endDate"
					/>
				</div>
			)}
		</div>
	);
}
